import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
  logIn,
  register,
  verifyEmail,
  send2FA,
  verify2FA,
  checkInviteToken,
  acceptInvite,
  sendDeclineReason,
  saveProfile,
  sendResetEmail,
  resetPassword,
  findInvite,
  setUserTheme,
  getProfile,
  getCaptchaRequirement,
  updatePassword,
} from './APIs/auth';
import {
  setCurrentUser,
  registerSuccess,
  userVerifyResponse,
  acceptInviteResult,
  setResetResult,
  setLoading,
  setError,
  setInviteData,
  mfaSentAction,
  mfaUpdateResult,
  setCaptchaRequired,
} from '../actions/authActions';
import types from '../constants/authConstants';
import { successNotif, failedNotif } from '../actions/notifActions';
import {
  clearAuthSession,
  persistAuthToken,
  persistAuthUserRecord,
  readStoredUser,
} from 'utils/authSession';
import { clearPostLoginReturnPath } from 'utils/postLoginReturnPath';
import {
  buildAuthUserFromSignIn,
  buildAuthUserFromProfile,
  buildAuthUserAfterMfaStep,
  buildAuthUserAfterPasswordUpdate,
} from 'utils/authUser';
import { changeDemoAction } from '../actions/uiActions';
import { clearDemoModePreference } from 'utils/demoModeStorage';
import { getApiErrorMessage } from 'utils/apiError';
import { isAdminUser } from 'utils/roleAccess';

const setToken = (token) => persistAuthToken(token);

function* enforceDemoAccessForUser(userRecord) {
  if (!isAdminUser(userRecord)) {
    clearDemoModePreference();
    yield put(changeDemoAction(false));
  }
}

function* persistSessionUser(userRecord) {
  persistAuthUserRecord(userRecord);
  yield put(setCurrentUser(userRecord));
  yield* enforceDemoAccessForUser(userRecord);
}

function resolveProfileSessionOverrides(previousUser, clearFlags = 'none') {
  if (clearFlags === 'all') {
    return { require2FA: false, requirePasswordUpdate: false };
  }
  if (clearFlags === 'mfa') {
    return {
      require2FA: false,
      requirePasswordUpdate: Boolean(previousUser?.requirePasswordUpdate),
    };
  }
  if (clearFlags === 'password') {
    return {
      require2FA: Boolean(previousUser?.require2FA),
      requirePasswordUpdate: false,
    };
  }
  if (previousUser) {
    return {
      require2FA: Boolean(previousUser.require2FA),
      requirePasswordUpdate: Boolean(previousUser.requirePasswordUpdate),
    };
  }
  return {};
}

function* verifyEmailWithCredential({ email }) {
  try {
    const response = yield verifyEmail(email);
    yield put(userVerifyResponse(response.data));
  } catch (error) {
    yield put(userVerifyResponse(null));
    yield put(failedNotif(getApiErrorMessage(error, 'Email verification failed.')));
  }
}

function* send2FACode({ payload }) {
  try {
    yield put(setLoading(true));

    const response = yield send2FA(payload);
    yield put(mfaSentAction(true));

    yield put(setLoading(false));
  } catch (error) {
    yield put(mfaSentAction('error'));
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to send verification code.')));
    yield put(setLoading(false));
  }
}

function* updateAuthentication({ payload }) {
  try {
    yield put(setLoading(true));
    yield put(setError(''));

    const response = yield updatePassword(payload);
    yield call(refreshUser, { payload: { clearFlags: 'password' } });
    yield put(successNotif(response?.message || 'Password updated successfully'));

    yield put(setLoading(false));
  } catch (error) {
    yield put(setError(getApiErrorMessage(error, 'Password update failed')));
    yield put(setLoading(false));
  }
}

function* verify2FACode({ payload }) {
  try {
    const response = yield verify2FA(payload);
    if (response?.data?.accessToken) {
      setToken(response.data.accessToken);
    }
    const previousUser = readStoredUser();
    if (previousUser) {
      yield* persistSessionUser({ ...previousUser, require2FA: false });
    }
    yield put(mfaUpdateResult('success'));
  } catch (error) {
    yield put(mfaUpdateResult('failed'));
    yield put(failedNotif(getApiErrorMessage(error, 'Invalid verification code.')));
  }
}

function* logInWithCredentials({ payload: { email, password, captcha_token } }) {
  try {
    yield put(setLoading(true));
    yield put(setError(''));

    const response = yield logIn(email, password, captcha_token);
    const signInData = response.data;
    setToken(signInData.accessToken);

    const userData = buildAuthUserFromSignIn(signInData);
    yield persistSessionUser(userData);

    if (userData.require2FA && userData.mfaMethod) {
      yield put(mfaSentAction(true));
    }

    yield put(setLoading(false));

    // Successful login without pending MFA/password steps — GuestRoute redirects to dashboard.
  } catch (error) {
    yield put(setError(getApiErrorMessage(error, 'Login failed')));

    if (error.response?.data?.requireCaptcha) {
      yield put(setCaptchaRequired(true));
    }
    yield put(setLoading(false));
  }
}

function* getCaptchaStatus() {
  try {
    const response = yield getCaptchaRequirement();
    yield put(setCaptchaRequired(response.required));
  } catch (error) {}
}

function* refreshUser({ payload } = {}) {
  try {
    const previousUser = readStoredUser();
    const clearFlags = payload?.clearFlags ?? 'none';
    const response = yield getProfile();

    let userData;
    if (clearFlags === 'password') {
      userData = buildAuthUserAfterPasswordUpdate(response.data, previousUser);
    } else {
      const overrides = resolveProfileSessionOverrides(previousUser, clearFlags);
      userData = buildAuthUserFromProfile(response.data, overrides);
    }

    yield persistSessionUser(userData);
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to refresh profile')));
  }
}

function* registerWithCredentials({
  payload: { email, password, username, role, landlordId, phonenumber, jobtitle, inviteToken },
}) {
  try {
    const response = yield register(
      username,
      email,
      password,
      role,
      landlordId,
      phonenumber,
      jobtitle,
      inviteToken,
    );
    yield put(successNotif(response.data.message));
    yield put(registerSuccess({ email, password }));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Registration failed.')));
  }
}

function* checkInviteTokenSaga({ payload }) {
  try {
    const response = yield checkInviteToken(payload);
    yield put(userVerifyResponse(response.data));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Invalid or expired invitation link.')));
  }
}

function* acceptInviteSaga({ payload }) {
  try {
    const response = yield acceptInvite(payload);
    yield put(
      acceptInviteResult({
        status: 'success',
        message: response.message,
        email: response.data?.email || null,
      }),
    );
  } catch (error) {
    yield put(
      acceptInviteResult({
        status: 'error',
        message: getApiErrorMessage(error, 'Unable to accept invitation.'),
        email: null,
      }),
    );
  }
}

function* sendDeclineReasonSaga({ payload }) {
  try {
    const response = yield sendDeclineReason(payload);
    yield put(successNotif(response.message));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to send decline reason.')));
  }
}

function* saveProfileSaga({ payload }) {
  try {
    yield put(setLoading(true));
    yield put(setError(''));
    const response = yield saveProfile(payload);
    yield put(successNotif(response.message));
    const previousUser = readStoredUser();
    const userData = buildAuthUserAfterMfaStep(response.data, previousUser);
    yield persistSessionUser(userData);
    yield put(setLoading(false));
  } catch (error) {
    const message = getApiErrorMessage(error, 'Failed to save profile.');
    yield put(setError(message));
    yield put(failedNotif(message));
    yield put(setLoading(false));
  }
}

function* sendResetEmailSaga({ payload }) {
  try {
    yield put(setLoading(true));
    yield put(setError(''));

    const response = yield sendResetEmail(payload);
    yield put(setResetResult('success'));

    yield put(setLoading(false));
  } catch (error) {
    yield put(setError(getApiErrorMessage(error, 'Failed to send reset email.')));
    yield put(setLoading(false));
  }
}

function* resetPasswordSaga({ payload }) {
  try {
    yield put(setLoading(true));
    yield put(setResetResult('loading'));
    const response = yield resetPassword(payload);
    yield put(setResetResult('success'));
    yield put(setLoading(false));
  } catch (error) {
    yield put(setError(getApiErrorMessage(error, 'Failed to reset password.')));
    yield put(setLoading(false));
  }
}

function* logInAfterRegister({ payload: { email, password } }) {
  yield logInWithCredentials({ payload: { email, password } });
}

function* onLogInStart() {
  yield takeLatest(types.LOG_IN_START, logInWithCredentials);
}

function* onGetCaptchaStatus() {
  yield takeLatest(types.GET_CAPTCHA_STATUS, getCaptchaStatus);
}

function* onRefreshUser() {
  yield takeLatest(types.REFRESH_USER, refreshUser);
}

function* onRegisterStart() {
  yield takeLatest(types.REGISTER_START, registerWithCredentials);
}

function* onRegisterSuccess() {
  yield takeLatest(types.REGISTER_SUCCESS, logInAfterRegister);
}

function* onVerifyEmail() {
  yield takeLatest(types.EMAIL_VERIFY, verifyEmailWithCredential);
}

function* onSend2FACode() {
  yield takeLatest(types.SEND_2FA_CODE, send2FACode);
}

function* onUpdateAuth() {
  yield takeLatest(types.UPDATE_AUTH, updateAuthentication);
}

function* onVerify2FACode() {
  yield takeLatest(types.VERIFY_2FA_CODE, verify2FACode);
}

function* onCheckInviteToken() {
  yield takeLatest(types.CHECK_INVITE_TOKEN, checkInviteTokenSaga);
}

function* onAcceptInvite() {
  yield takeLatest(types.ACCEPT_INVITE, acceptInviteSaga);
}

function* onSendDeclineReason() {
  yield takeLatest(types.SEND_DECLINE_REASON, sendDeclineReasonSaga);
}

function* onSaveProfile() {
  yield takeLatest(types.SAVE_PROFILE, saveProfileSaga);
}

function* onSendResetEmail() {
  yield takeLatest(types.SEND_RESET_EMAIL, sendResetEmailSaga);
}

function* onResetPassword() {
  yield takeLatest(types.RESET_PASSWORD, resetPasswordSaga);
}

function* findInviteSaga({ payload }) {
  try {
    yield put(setLoading(true));
    yield put(setError(''));

    const response = yield findInvite(payload);
    yield put(setInviteData(response.message));
    yield put(setLoading(false));
  } catch (error) {
    yield put(setError(getApiErrorMessage(error, 'Unable to find invitation. Please try again.')));
    yield put(setLoading(false));
  }
}

function* onFindInvite() {
  yield takeLatest(types.FIND_INVITE, findInviteSaga);
}

function* setUserThemeSaga({ payload }) {
  try {
    const previousUser = readStoredUser();
    const response = yield setUserTheme({ payload });
    const overrides = resolveProfileSessionOverrides(previousUser, 'none');
    const userData = buildAuthUserFromProfile(response.data, overrides);
    yield persistSessionUser(userData);
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to update theme')));
  }
}

function* onSetUserTheme() {
  yield takeLatest(types.SET_USER_THEME, setUserThemeSaga);
}

function* handleLogOut() {
  clearPostLoginReturnPath();
  clearAuthSession();
}

function* onLogOut() {
  yield takeLatest(types.LOG_OUT, handleLogOut);
}

export default function* authSagas() {
  yield all([
    call(onLogInStart),
    call(onGetCaptchaStatus),
    call(onRefreshUser),
    call(onRegisterStart),
    call(onRegisterSuccess),
    call(onVerifyEmail),
    call(onUpdateAuth),
    call(onSend2FACode),
    call(onVerify2FACode),
    call(onCheckInviteToken),
    call(onAcceptInvite),
    call(onSendDeclineReason),
    call(onSaveProfile),
    call(onSendResetEmail),
    call(onResetPassword),
    call(onFindInvite),
    call(onSetUserTheme),
    call(onLogOut),
  ]);
}
